import React, { useState, forwardRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Check, AlertCircle, Phone } from "lucide-react";
import { useLang } from "../i18n/LangProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reservation = forwardRef(({ enabled = true }, ref) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "19:30",
    guests: 2,
    notes: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === "guests" ? Number(value) : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/reservations`, form);
      setStatus({ state: "success", msg: t.reservation.success });
      setForm({
        name: "",
        email: "",
        phone: "",
        date: "",
        time: "19:30",
        guests: 2,
        notes: "",
      });
    } catch (err) {
      console.error(err);
      setStatus({ state: "error", msg: t.reservation.error });
    }
  };

  // Generate time options
  const times = [];
  for (let h = 12; h <= 22; h++) {
    for (const m of [0, 30]) {
      if (h === 14 && m === 30) continue;
      if (h === 15 || h === 16 || h === 17 || h === 18) continue;
      times.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  const today = new Date().toISOString().split("T")[0];

  return (
    <section
      id="reservation"
      ref={ref}
      data-testid="reservation-section"
      className="bg-surface py-24 md:py-32 relative grain"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <div className="flex items-center gap-3 justify-center mb-6">
            <span className="divider-line" />
            <span className="overline">{t.reservation.overline}</span>
            <span className="divider-line" />
          </div>
          <h2 className="font-serif font-light text-ivory text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            {t.reservation.title}
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-ivory/65 text-base sm:text-lg leading-relaxed">
            {enabled ? t.reservation.subtitle : t.reservation.closedText}
          </p>
        </div>

        {!enabled ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            data-testid="reservation-closed"
            className="bg-obsidian border border-edge p-10 md:p-16 text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 border border-copper/40 mb-8">
              <Phone size={22} className="text-copper" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl text-ivory mb-4">
              {t.reservation.closedTitle}
            </h3>
            <p className="max-w-md mx-auto text-ivory/65 mb-10 leading-relaxed">
              {t.reservation.closedText}
            </p>
            <a
              href="tel:0227981011"
              data-testid="reservation-call-button"
              className="btn-primary"
            >
              <Phone size={16} /> 022 798 10 11
            </a>
          </motion.div>
        ) : (
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          onSubmit={onSubmit}
          data-testid="reservation-form"
          className="bg-obsidian border border-edge p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-7">
            <div>
              <label className="overline block mb-3">{t.reservation.name}</label>
              <input
                data-testid="reservation-name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={onChange}
                className="input-elegant"
                placeholder="Jean Dupont"
              />
            </div>
            <div>
              <label className="overline block mb-3">{t.reservation.email}</label>
              <input
                data-testid="reservation-email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={onChange}
                className="input-elegant"
                placeholder="jean@email.com"
              />
            </div>
            <div>
              <label className="overline block mb-3">{t.reservation.phone}</label>
              <input
                data-testid="reservation-phone"
                name="phone"
                type="tel"
                required
                value={form.phone}
                onChange={onChange}
                className="input-elegant"
                placeholder="+41 22 ..."
              />
            </div>
            <div>
              <label className="overline block mb-3">{t.reservation.guests}</label>
              <select
                data-testid="reservation-guests"
                name="guests"
                value={form.guests}
                onChange={onChange}
                className="input-elegant bg-obsidian appearance-none cursor-pointer"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((n) => (
                  <option key={n} value={n} className="bg-obsidian">
                    {n} {n === 1 ? t.reservation.person : t.reservation.persons}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-3">{t.reservation.date}</label>
              <input
                data-testid="reservation-date"
                name="date"
                type="date"
                required
                min={today}
                value={form.date}
                onChange={onChange}
                className="input-elegant"
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div>
              <label className="overline block mb-3">{t.reservation.time}</label>
              <select
                data-testid="reservation-time"
                name="time"
                value={form.time}
                onChange={onChange}
                className="input-elegant bg-obsidian appearance-none cursor-pointer"
              >
                {times.map((t) => (
                  <option key={t} value={t} className="bg-obsidian">
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-3">{t.reservation.notes}</label>
              <textarea
                data-testid="reservation-notes"
                name="notes"
                rows={3}
                value={form.notes}
                onChange={onChange}
                className="input-elegant resize-none"
                placeholder="..."
              />
            </div>
          </div>

          {status.state === "success" && (
            <div
              data-testid="reservation-success"
              className="mt-8 flex items-center gap-3 text-copper-soft border border-copper/40 bg-copper/5 px-5 py-4"
            >
              <Check size={18} />
              <span className="text-sm">{status.msg}</span>
            </div>
          )}
          {status.state === "error" && (
            <div
              data-testid="reservation-error"
              className="mt-8 flex items-center gap-3 text-wine border border-wine/40 bg-wine/5 px-5 py-4"
            >
              <AlertCircle size={18} />
              <span className="text-sm">{status.msg}</span>
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <p className="text-xs text-ivory/50 max-w-sm leading-relaxed">
              {t.contact.phoneLabel}: <span className="text-copper">022 798 10 11</span>
            </p>
            <button
              data-testid="reservation-submit-button"
              type="submit"
              disabled={status.state === "loading"}
              className="btn-primary disabled:opacity-60"
            >
              {status.state === "loading"
                ? t.reservation.submitting
                : t.reservation.submit}{" "}
              <Send size={14} />
            </button>
          </div>
        </motion.form>
        )}
      </div>
    </section>
  );
});

Reservation.displayName = "Reservation";

export default Reservation;
